import { useEffect, useState } from 'react';
import { Dialog, MenuItem, Select, TextField } from '@mui/material';

import { IncidentStatuses, type Incident, type IncidentStatus } from '@models/incidents';
import { INCIDENT_STATUS_LABELS } from '@features/incidents/constants';
import {
  useCreateIncidentMutation,
  useUpdateIncidentMutation,
} from '@features/incidents/mutations';

import s from './IncidentFormDialog.module.css';

const NAME_MAX_LENGTH = 200;

type IncidentFormDialogProps = {
  open: boolean;
  onClose: () => void;
  /** When set the dialog edits this incident; otherwise it creates a new one. */
  incident?: Incident | null;
  onSaved?: (incident: Incident) => void;
};

function IncidentFormDialog({ open, onClose, incident, onSaved }: IncidentFormDialogProps) {
  const [name, setName] = useState('');
  const [status, setStatus] = useState<IncidentStatus>(IncidentStatuses.OPEN);
  const [description, setDescription] = useState('');
  const createIncident = useCreateIncidentMutation();
  const updateIncident = useUpdateIncidentMutation();

  const isEdit = Boolean(incident);
  const isPending = createIncident.isPending || updateIncident.isPending;

  // Re-seed the fields every time the dialog opens so a cancelled edit doesn't leak into the next one.
  useEffect(() => {
    if (!open) return;
    setName(incident?.name ?? '');
    setStatus(incident?.status ?? IncidentStatuses.OPEN);
    setDescription(incident?.description ?? '');
  }, [open, incident]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const hasChanges =
    !incident ||
    trimmedName !== incident.name ||
    status !== incident.status ||
    trimmedDescription !== (incident.description ?? '');
  const canSave = trimmedName.length > 0 && hasChanges && !isPending;

  const handleSave = () => {
    if (!canSave) return;
    const payload = {
      name: trimmedName,
      status,
      description: trimmedDescription.length > 0 ? trimmedDescription : null,
    };

    if (incident) {
      updateIncident.mutate(
        { id: incident.id, payload },
        {
          onSuccess: (saved) => {
            onSaved?.(saved);
            onClose();
          },
        },
      );
      return;
    }

    createIncident.mutate(payload, {
      onSuccess: (saved) => {
        onSaved?.(saved);
        onClose();
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '14px',
            width: 520,
            maxWidth: '92vw',
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.08)',
          },
        },
      }}>
      <form
        className={s.body}
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}>
        <h3 className={s.title}>{isEdit ? 'Edit incident' : 'New incident'}</h3>
        <p className={s.subtitle}>
          {isEdit
            ? 'Rename, restate or move the incident along.'
            : 'Give the incident a name operators will recognise; attach customers afterwards.'}
        </p>

        <div className={s.field}>
          <span className={s.fieldLabel}>Name</span>
          <TextField
            value={name}
            onChange={(event) => setName(event.target.value.slice(0, NAME_MAX_LENGTH))}
            size="small"
            fullWidth
            autoFocus
            placeholder="e.g. Migration race double charges (Aug 2026)"
          />
        </div>

        <div className={s.field}>
          <span className={s.fieldLabel}>Status</span>
          <Select
            value={status}
            size="small"
            fullWidth
            onChange={(event) => setStatus(event.target.value as IncidentStatus)}>
            {Object.values(IncidentStatuses).map((value) => (
              <MenuItem key={value} value={value}>
                {INCIDENT_STATUS_LABELS[value]}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className={s.field}>
          <span className={s.fieldLabel}>Description</span>
          <TextField
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            placeholder="What happened, who was affected, what the repair does."
          />
        </div>

        <div className={s.actions}>
          <button type="button" className={s.cancelBtn} onClick={onClose} disabled={isPending}>
            Cancel
          </button>
          <button type="submit" className={s.saveBtn} disabled={!canSave}>
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create incident'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export default IncidentFormDialog;
